import { SUPPORTED_TIME_ZONES } from './supported-time-zones';

export type TimeZoneOption = { label: string; value: string };

// Hermes (React Native) and jsdom do not implement supportedValuesOf. That used to leave the
// picker empty, which on mobile meant the field could never be set at all — so the shipped list
// is the fallback. It is the same set either way; the engine is only consulted to drop zones its
// tzdata does not carry, which would otherwise be offered and then fail to render a time.
const getSupportedTimeZones = (): string[] => {
  const zones = Array.from(SUPPORTED_TIME_ZONES);
  if (typeof Intl.supportedValuesOf !== 'function') {
    return zones;
  }
  // Narrowed to the zones the backend will accept back. An engine lists every zone in its tzdata,
  // which is a superset: Microsoft Graph stores an event's zone as a Windows name and a few IANA
  // zones have no Windows equivalent, so offering one would only produce a save that fails.
  const engineZones = new Set(Intl.supportedValuesOf('timeZone'));
  return zones.filter((zone) => engineZones.has(zone));
};

const toLabel = (zone: string) => zone.replace(/_/g, ' ');

// `alwaysInclude` carries the event's own zone and the browser's: a provider can return a
// zone this engine does not list, and dropping it would silently reset the field on save.
// Deliberately NOT filtered against the supported set — these are values already in play, and
// hiding the one currently on the event would misrepresent it as unset.
export const getTimeZoneOptions = (
  alwaysInclude: (string | null | undefined)[] = [],
): TimeZoneOption[] => {
  const zones = new Set(getSupportedTimeZones());
  alwaysInclude.forEach((zone) => zone && zones.add(zone));

  return Array.from(zones)
    .sort((a, b) => a.localeCompare(b))
    .map((zone) => ({ label: toLabel(zone), value: zone }));
};
